import { useEffect, useState } from 'react';
import type { Editor } from '../editor';
import { formatLength } from '../model/units';

/**
 * 상태바 (작업지시서 P2-16)
 *
 * 좌표 · 각 모드 토글 · 단위 · 축척 · 객체 수.
 * 기능키(F3/F8/F9/F10)와 1:1 대응하며, 클릭으로도 토글된다 —
 * CAD 사용자는 손이 기능키를 기억하지만 신규 사용자는 여기를 누른다.
 */
export function StatusBar({ editor }: { editor: Editor }) {
  const [, setTick] = useState(0);
  useEffect(() => editor.subscribe(() => setTick((t) => t + 1)), [editor]);

  const s = editor.snapSettings;
  const unit = editor.scene.doc.displayUnit;
  const c = editor.cursor;

  return (
    <div className="statusbar">
      <span className="statusbar__coord" title="커서 좌표">
        {c
          ? `X ${formatLength(c.x, unit)}   Y ${formatLength(c.y, unit)}`
          : `X —   Y —`}
      </span>

      <span className="statusbar__sep" />

      <Toggle label="스냅" hint="F9 — 격자에 맞춰 이동" on={s.gridSnap} onClick={() => editor.toggleGridSnap()} />
      <Toggle label="직교" hint="F8 — 수평/수직으로만" on={s.ortho} onClick={() => editor.toggleOrtho()} />
      <Toggle label="극좌표" hint="F10 — 일정 각도로만" on={s.polar} onClick={() => editor.togglePolar()} />
      <Toggle label="객체스냅" hint="F3 — 끝점·중점·교차점에 붙이기" on={s.osnap} onClick={() => editor.toggleOsnap()} />

      <span className="statusbar__spacer" />

      <span className="statusbar__item" title="선택된 객체 수">
        선택 {editor.selection.size}
      </span>
      <span className="statusbar__item" title="도면 전체 객체 수">
        객체 {editor.scene.count.toLocaleString('ko-KR')}
      </span>
      <span className="statusbar__item" title="현재 화면 축척">
        {editor.viewport.scaleLabel()}
      </span>
      <span className="statusbar__item" title="도면 단위">
        {unit}
      </span>
    </div>
  );
}

function Toggle({
  label,
  hint,
  on,
  onClick,
}: {
  label: string;
  hint: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="statusbar__toggle"
      aria-pressed={on}
      title={hint}
      onClick={onClick}
    >
      {/* 색상만으로 구분하지 않는다 — 접근성 요구 */}
      <span aria-hidden="true">{on ? '●' : '○'}</span> {label}
    </button>
  );
}
